import { useCallback } from 'react';
import { usePhotoStorage, StoredPhoto } from './usePhotoStorage';

interface PhotoProcessorOptions {
  targetSize?: number;
  quality?: number;
}

export function usePhotoProcessor(formKey: string, options: PhotoProcessorOptions = {}) {
  const { loadPhoto, savePhotoData } = usePhotoStorage(formKey);
  const targetSize = options.targetSize ?? 1024;
  const quality = options.quality ?? 0.75;

  const processPhoto = useCallback(async (): Promise<StoredPhoto | null> => {
    const current = await loadPhoto();
    if (!current || current.processed) {
      return current ?? null;
    }

    const img = new Image();
    img.src = current.data;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (err) => reject(err);
    });

    const size = Math.min(img.width, img.height);
    const offsetX = (img.width - size) / 2;
    const offsetY = (img.height - size) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.warn('Canvas context unavailable for photo processing');
      return current;
    }

    ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, targetSize, targetSize);
    const compressedData = canvas.toDataURL('image/jpeg', quality);

    const updated: StoredPhoto = {
      ...current,
      data: compressedData,
      processed: true,
      processedAt: new Date().toISOString(),
    };

    await savePhotoData(updated);
    return updated;
  }, [loadPhoto, quality, savePhotoData, targetSize]);

  return { processPhoto };
}

